import axios from "axios";
import {OAuthPopupError, type OAuthProvider} from "../services/oauthProviders.ts";

export const EMAIL_NOT_VERIFIED_MESSAGE =
  "We couldn't verify the email on this account. Use your password or contact your workspace admin.";
export const WORKSPACE_CLOSED_MESSAGE = "This workspace is closed.";
export const WORKSPACE_CREATE_FAILED_MESSAGE = "We couldn't create your workspace. Please try again.";
export const LINK_CONFLICT_MESSAGE = "This account can't be linked automatically. Contact your workspace admin.";
export const ADMIN_CONSENT_MESSAGE =
  "Your organization requires admin approval for Microsoft sign-in. Use your password or ask your IT admin.";
export const POPUP_BLOCKED_MESSAGE =
  "Your browser blocked the sign-in popup. Allow popups for this site and try again.";
export const INTERACTION_IN_PROGRESS_MESSAGE =
  "A Microsoft sign-in is already in progress. Finish it or reload the page.";

export class InvalidProviderLoginResponseError extends Error {
  constructor() {
    super("Provider login response is malformed");
    this.name = "InvalidProviderLoginResponseError";
  }
}

export const providerLabel = (provider: OAuthProvider): string =>
  provider === "google" ? "Google" : "Microsoft";

export const providerUnavailableMessage = (provider: OAuthProvider): string =>
  `${providerLabel(provider)} sign-in isn't available right now.`;

export const providerVerifyFailedMessage = (provider: OAuthProvider): string =>
  `Could not verify your ${providerLabel(provider)} sign-in. Please try again.`;

const axiosErrorDetail = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return "";

  const data = error.response?.data;
  return data && typeof data === "object" && typeof data.detail === "string" ? data.detail : "";
};

export const providerLoginErrorMessage = (provider: OAuthProvider, error: unknown): string | null => {
  if (error instanceof InvalidProviderLoginResponseError) {
    return providerVerifyFailedMessage(provider);
  }

  if (error instanceof OAuthPopupError) {
    switch (error.reason) {
      case "cancelled":
        return null;
      case "blocked":
        return POPUP_BLOCKED_MESSAGE;
      case "in_progress":
        return INTERACTION_IN_PROGRESS_MESSAGE;
      case "admin_consent":
        return ADMIN_CONSENT_MESSAGE;
      case "unavailable":
        return providerUnavailableMessage(provider);
    }
  }

  if (!axios.isAxiosError(error)) return "Login failed. Please try again.";

  const status = error.response?.status;
  const detail = axiosErrorDetail(error);
  if (status === 403 && detail === "Email not verified") return EMAIL_NOT_VERIFIED_MESSAGE;
  if (status === 403 && detail === "Account is closed") return WORKSPACE_CLOSED_MESSAGE;
  if (status === 409) return LINK_CONFLICT_MESSAGE;
  if (status === 401) return providerVerifyFailedMessage(provider);
  if (status === 503 && detail === "Could not create workspace") return WORKSPACE_CREATE_FAILED_MESSAGE;
  if (status === 503) return providerUnavailableMessage(provider);
  return "Login failed. Please try again.";
};
